import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

/**
 * Custom hook for navigation that automatically prefixes routes with the active tenancy slug
 */
export function useSlugNavigate() {
  const navigate = useNavigate()
  const { getActiveSlug } = useAuth()

  /**
   * Navigate to a path, automatically prefixing with the active tenancy slug
   * @param {string} path - The path to navigate to (e.g., '/settings', '/migrations')
   * @param {object} options - Navigation options (same as react-router navigate)
   */
  const slugNavigate = (path, options) => {
    const slug = getActiveSlug()

    // Don't prefix public routes
    const publicRoutes = ['/login', '/logout', '/auth/verify']
    if (publicRoutes.some(route => path.startsWith(route))) {
      navigate(path, options)
      return
    }

    // Prefix with slug for protected routes
    if (slug) {
      const prefixedPath = path === '/' ? `/${slug}` : `/${slug}${path}`
      navigate(prefixedPath, options)
    } else {
      // Fallback to original path if no slug (shouldn't happen after onboarding)
      navigate(path, options)
    }
  }

  return slugNavigate
}

/**
 * Helper to build a slug-prefixed path
 * @param {string} slug - The tenancy slug
 * @param {string} path - The path (e.g., '/settings')
 * @returns {string} The prefixed path
 */
export function buildSlugPath(slug, path) {
  if (!slug) return path
  return path === '/' ? `/${slug}` : `/${slug}${path}`
}
